<template>
  <div ref="wrapRef" class="wrap" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave" @click="handleClick">
    <div ref="contentRef" class="content" :class="animationClass" :style="contentStyle" @animationend="onAnimationEnd">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, toRefs, reactive, watch, nextTick, onUnmounted } from 'vue'

const props = defineProps({
  content: { type: Array, default: () => [] },
  delay: { type: Number, default: 0.5 },
  speed: { type: Number, default: 100 },
  direction: { type: String, default: 'horizontal' }, // 新增方向属性
  wrapWidth: { type: Number, default: 0 },
  wrapHeight: { type: Number, default: 0 },
  scrollDirection: { type: String, default: 'rtl' } // 新增滚动方向属性，默认为从右往左
})

const state = reactive({
  wrapWidth: Number(props.wrapWidth),
  wrapHeight: Number(props.wrapHeight), // 新增高度
  duration: 0,
  contentWidth: 0,
  contentHeight: 0, // 新增内容高度
  animationClass: '',
  delay: Number(props.delay),
  speed: Number(props.speed),
  isPaused: true,
  autoResumeTimeout: null,
})

const { animationClass } = toRefs(state)

const wrapRef = ref(null)
const contentRef = ref(null)

const contentStyle = computed(() => {
  const startHorizontal = props.scrollDirection === 'rtl' ? '0%' : '-100%'
  const endHorizontal = props.scrollDirection === 'rtl' ? '-100%' : '0%'

  return {
    flexDirection: props.direction === 'horizontal' ? props.scrollDirection === 'rtl' ? 'row' : 'row-reverse' : 'column',
    paddingLeft: props.direction === 'horizontal' ? `${state.wrapWidth}px` : '0',
    paddingTop: props.direction === 'vertical' ? `${state.wrapHeight}px` : '0',
    animationDelay: `${state.delay || 0.5}s`,
    animationDuration: `${state.duration}s`,
    animationPlayState: state.isPaused ? 'paused' : 'running',
    '--start-position-horizontal': startHorizontal,
    '--end-position-horizontal': endHorizontal,
  }
})

const setAnimationClass = () => { 
  nextTick(() => { 
    state.animationClass = props.direction === 'vertical' ? 'animate-vertical' : 'animate'
    state.isPaused = false
  })
}

const calculateSizes = () => {
  nextTick(() => {
    if (props.direction === 'horizontal') {
      const wrapWidth = props.wrapWidth || wrapRef.value.getBoundingClientRect().width
      const contentWidth = contentRef.value.getBoundingClientRect().width
      state.wrapWidth = wrapWidth
      state.contentWidth = contentWidth
      state.duration = (state.contentWidth + state.wrapWidth) / state.speed // 水平滚动
    } else {
      const wrapHeight = props.wrapHeight || wrapRef.value.getBoundingClientRect().height
      const contentHeight = contentRef.value.scrollHeight
      state.wrapHeight = wrapHeight
      state.contentHeight = contentHeight
      state.duration = (state.contentHeight + state.wrapHeight) / (state.speed) // 垂直滚动，减慢速度
    }
    setTimeout(() => {
      setAnimationClass()
    }, 500)
  })
}

const handleMouseEnter = () => {
  state.isPaused = true
}

const handleMouseLeave = () => {
  state.isPaused = false
}

const handleClick = () => {
  state.isPaused = !state.isPaused

  if (!state.isPaused) {
    clearTimeout(state.autoResumeTimeout)
    state.autoResumeTimeout = setTimeout(() => {
      state.isPaused = false
    }, 10000) // 10秒后自动恢复
  }
}

const onAnimationEnd = () => {
  if (props.direction === 'horizontal') {
    state.duration = (state.contentWidth + state.wrapWidth) / state.speed
    state.animationClass = 'animate-infinite'
  } else {
    state.duration = (state.contentHeight + state.wrapHeight) / (state.speed) // 垂直滚动，减慢速度
    state.animationClass = 'animate-infinite-vertical'
  }
}

onUnmounted(() => {
  clearTimeout(state.autoResumeTimeout)
})

watch(() => props.content, (newContent, oldContent) => {
  if (newContent !== oldContent && newContent.length > 0) {
    calculateSizes()
  }
}, { immediate: true, deep: true })

watch(() => [props.wrapWidth, props.wrapHeight, props.delay, props.speed, props.direction, props.scrollDirection], () => {
  state.wrapWidth = props.wrapWidth
  state.wrapHeight = props.wrapHeight
  state.delay = props.delay
  state.speed = props.speed
  calculateSizes()
}, { deep: true })

</script>

<style lang="scss" scoped>
.wrap {
  width: 100%;
  height: 100%;
  line-height: 30px;
  overflow: hidden;
  color: #090808;
  position: relative;
}

.content {
  height: 100%;
  position: absolute;
  white-space: nowrap;
  display: flex;
}

.animate {
  animation: marquee linear;
}

.animate-infinite {
  animation: marquee linear infinite;
}

.animate-vertical {
  animation: marquee-vertical linear;
}

.animate-infinite-vertical {
  animation: marquee-vertical linear infinite;
}

@keyframes marquee {
  0% {
    transform: translateX(var(--start-position-horizontal, 100%));
  }

  100% {
    transform: translateX(var(--end-position-horizontal, -100%));
  }
}

@keyframes marquee-vertical {
  0% {
    transform: translateY(100%);
  }

  100% {
    transform: translateY(-100%);
  }
}
</style>
